<template>
  <div class="show_all_content_wrapper">
    <main>
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>

        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>

        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <base-input
                v-if="activeReportType === 'tasks'"
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.taskName')"
                v-model="filterOptions.taskName"
              />

              <base-input
                v-if="activeReportType === 'tasks'"
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.facilityName')"
                v-model="filterOptions.facilityName"
              />

              <base-input
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.employee_name')"
                v-model="filterOptions.employeeName"
              />

              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.time_from')"
                v-model.trim="filterOptions.from"
              />

              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.time_to')"
                v-model.trim="filterOptions.to"
              />
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="loading">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="loading"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>

      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.task_reports_management") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <base-button
            class="mt-0 pdf_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadPdf')"
            @fireClick="downloadPdf"
            :disabled="!tableRows.length"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-pdf"></i>
            </template>
          </base-button>

          <base-button
            class="mt-0 excel_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadExcel')"
            @fireClick="downloadExcel"
            :disabled="!tableRows.length"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-excel"></i>
            </template>
          </base-button>
        </div>
      </div>

      <div class="report_type_switch mb-3">
        <button
          v-if="canViewTaskReports"
          type="button"
          class="report_type_btn"
          :class="{ active: activeReportType === 'tasks' }"
          @click="changeReportType('tasks')"
        >
          {{ $t("PLACEHOLDERS.tasks_report") }}
        </button>
        <button
          v-if="canViewEmployeeReports"
          type="button"
          class="report_type_btn"
          :class="{ active: activeReportType === 'employees' }"
          @click="changeReportType('employees')"
        >
          {{ $t("PLACEHOLDERS.employee_performance_report") }}
        </button>
      </div>

      <v-data-table
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :headers="tableHeaders"
        :items="tableRows"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <template v-slot:[`item.serial_number`]="{ index }">
          {{
            (paginations.current_page - 1) * paginations.items_per_page +
            index +
            1
          }}
        </template>

        <template v-slot:[`item.employees`]="{ item }">
          {{
            item.employees && item.employees.length
              ? item.employees.join("، ")
              : "-"
          }}
        </template>

        <template v-slot:[`item.job_name`]="{ item }">
          {{ item.job && item.job.name ? item.job.name : "-" }}
        </template>

        <template v-slot:no-data>
          {{ noDataMessage }}
        </template>
      </v-data-table>
    </main>

    <div class="pagination_container text-center mt-3 mb-0">
      <v-pagination
        class="py-0"
        square
        v-model="paginations.current_page"
        :length="paginations.last_page"
        :total-visible="6"
        @input="updateRouterQueryParam($event)"
        :prev-icon="
          getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
        "
        :next-icon="
          getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
        "
      />
    </div>

    <vue-html2pdf
      :show-layout="false"
      :float-layout="true"
      :enable-download="true"
      :preview-modal="true"
      :filename="pdfFileName"
      :pdf-quality="2"
      pdf-format="a4"
      :manual-pagination="false"
      :paginate-elements-by-height="1400"
      pdf-content-width="100%"
      @hasGenerated="$message.success($t('MESSAGES.generatedSuccessfully'))"
      ref="html2Pdf"
    >
      <section slot="pdf-content">
        <div class="pdf_file_content">
          <h3 class="file_title">{{ reportTitle }}</h3>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th v-for="header in tableHeaders" :key="header.value">
                    <span>{{ header.text }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in pdfRows" :key="row.key">
                  <td>{{ index + 1 }}</td>
                  <td
                    v-for="(cell, cellIndex) in row.cells"
                    :key="`${row.key}-${cellIndex}`"
                  >
                    {{ cell }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </section>
    </vue-html2pdf>
  </div>
</template>

<script>
import VueHtml2pdf from "vue-html2pdf";
import { mapGetters } from "vuex";
import * as XLSX from "xlsx";

export default {
  name: "AllTaskReports",

  components: {
    VueHtml2pdf,
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    reportTitle() {
      return this.activeReportType === "tasks"
        ? this.$t("PLACEHOLDERS.tasks_report")
        : this.$t("PLACEHOLDERS.employee_performance_report");
    },

    pdfFileName() {
      return this.reportTitle;
    },

    canViewTaskReports() {
      return this.$can("task reports index", "task-reports");
    },

    canViewEmployeeReports() {
      return this.$can("employee reports index", "employee-reports");
    },

    hasAnyReportPermission() {
      return this.canViewTaskReports || this.canViewEmployeeReports;
    },

    hasActiveFilters() {
      if (this.activeReportType === "tasks") {
        return !!(
          this.filterOptions.taskName ||
          this.filterOptions.facilityName ||
          this.filterOptions.employeeName ||
          this.filterOptions.from ||
          this.filterOptions.to
        );
      }

      return !!(
        this.filterOptions.employeeName ||
        this.filterOptions.from ||
        this.filterOptions.to
      );
    },

    noDataMessage() {
      return this.hasActiveFilters
        ? this.$t("MESSAGES.noSearchResults")
        : this.$t("MESSAGES.noReportsAvailable");
    },

    tableHeaders() {
      if (this.activeReportType === "tasks") {
        return [
          {
            text: this.$t("TABLES.Admins.serialNumber"),
            value: "serial_number",
            align: "center",
            sortable: false,
          },
          {
            text: this.$t("PLACEHOLDERS.taskName"),
            value: "task_name",
            align: "center",
            sortable: false,
          },
          {
            text: this.$t("PLACEHOLDERS.startTime"),
            value: "start_time",
            align: "center",
            sortable: false,
          },
          {
            text: this.$t("PLACEHOLDERS.endTime"),
            value: "end_time",
            align: "center",
            sortable: false,
          },
          {
            text: this.$t("PLACEHOLDERS.facilityName"),
            value: "facility_name",
            align: "center",
            sortable: false,
          },
          {
            text: this.$t("PLACEHOLDERS.employees_count_report"),
            value: "employees_count",
            align: "center",
            sortable: false,
          },
          {
            text: this.$t("PLACEHOLDERS.task_employees"),
            value: "employees",
            align: "center",
            sortable: false,
          },
          {
            text: this.$t("PLACEHOLDERS.completed_employees"),
            value: "completed_employees",
            align: "center",
            sortable: false,
          },
        ];
      }

      return [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "serial_number",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.employee_name"),
          value: "employee_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.job"),
          value: "job_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.total_tasks_assigned"),
          value: "total_tasks",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.completed_tasks_report"),
          value: "completed_tasks",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.ongoing_tasks_report"),
          value: "ongoing_tasks",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.final_result"),
          value: "performance",
          align: "center",
          sortable: false,
        },
      ];
    },

    pdfRows() {
      if (this.activeReportType === "tasks") {
        return this.tableRows.map((row) => ({
          key: row.id,
          cells: [
            row.task_name || "-",
            row.start_time || "-",
            row.end_time || "-",
            row.facility && row.facility.name ? row.facility.name : "-",
            row.employees_count ?? 0,
            row.employees && row.employees.length
              ? row.employees.join(", ")
              : "-",
            row.completed_employees ?? 0,
          ],
        }));
      }

      return this.tableRows.map((row, index) => ({
        key: `${row.employee_name}-${index}`,
        cells: [
          row.employee_name || "-",
          row.job && row.job.name ? row.job.name : "-",
          row.total_tasks ?? 0,
          row.completed_tasks ?? 0,
          row.ongoing_tasks ?? 0,
          row.performance || "-",
        ],
      }));
    },
  },

  data() {
    return {
      loading: false,
      filterFormIsActive: false,
      activeReportType: "tasks",
      excelUrl: null,
      filterOptions: {
        taskName: null,
        facilityName: null,
        employeeName: null,
        from: null,
        to: null,
      },
      tableRows: [],
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 15,
      },
    };
  },

  methods: {
    async submitFilterForm() {
      this.paginations.current_page = 1;
      await this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions.taskName = null;
      this.filterOptions.facilityName = null;
      this.filterOptions.employeeName = null;
      this.filterOptions.from = null;
      this.filterOptions.to = null;
      this.paginations.current_page = 1;
      await this.setTableRows();
    },

    changeReportType(type) {
      if (
        (type === "tasks" && !this.canViewTaskReports) ||
        (type === "employees" && !this.canViewEmployeeReports) ||
        this.activeReportType === type
      ) {
        return;
      }
      this.activeReportType = type;
      this.paginations.current_page = 1;
      this.updateRouterQueryParam(1);
    },

    getRequestConfig() {
      const commonParams = {
        page: this.paginations.current_page,
        employeeName: this.filterOptions.employeeName,
        from: this.filterOptions.from,
        to: this.filterOptions.to,
      };

      if (this.activeReportType === "tasks") {
        return {
          url: "financial-reports/tasks",
          params: {
            ...commonParams,
            taskName: this.filterOptions.taskName,
            facilityName: this.filterOptions.facilityName,
          },
        };
      }

      return {
        url: "financial-reports/employees",
        params: commonParams,
      };
    },

    normalizeTableRows(rows) {
      if (this.activeReportType !== "tasks") {
        return rows || [];
      }

      return (rows || []).map((row) => ({
        ...row,
        facility_name:
          row.facility && row.facility.name ? row.facility.name : "-",
      }));
    },

    async setTableRows() {
      if (!this.hasAnyReportPermission) {
        return;
      }

      this.loading = true;
      try {
        const requestConfig = this.getRequestConfig();
        const res = await this.$axios({
          method: "GET",
          url: requestConfig.url,
          params: requestConfig.params,
        });

        const payload = res.data?.data || {};
        const dataRows = payload.data || [];
        const meta = payload.meta || {};

        this.tableRows = this.normalizeTableRows(dataRows);
        this.excelUrl = payload.excel || null;
        this.paginations.last_page = meta.last_page || 1;
        this.paginations.items_per_page = meta.per_page || 15;
      } catch (error) {
        this.tableRows = [];
        this.excelUrl = null;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorLoadingData")
        );
      } finally {
        this.loading = false;
      }
    },

    async downloadPdf() {
      await this.$refs.html2Pdf.generatePdf();
    },

    downloadExcelFromRows() {
      const formattedRows = this.pdfRows.map((row, index) => {
        const mapped = {
          [this.$t("TABLES.Admins.serialNumber")]: index + 1,
        };

        this.tableHeaders
          .filter((header) => header.value !== "serial_number")
          .forEach((header, headerIndex) => {
            mapped[header.text] = row.cells[headerIndex] ?? "-";
          });

        return mapped;
      });

      const worksheet = XLSX.utils.json_to_sheet(formattedRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, this.reportTitle);

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const data = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${this.reportTitle}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },

    async downloadExcel() {
      if (this.excelUrl) {
        const link = document.createElement("a");
        link.href = this.excelUrl;
        link.setAttribute("target", "_blank");
        document.body.appendChild(link);
        link.click();
        link.remove();
        return;
      }

      this.downloadExcelFromRows();
    },

    async updateRouterQueryParam(pagenationValue) {
      this.paginations.current_page = pagenationValue;
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
          type: this.activeReportType,
        },
      });

      await this.setTableRows();
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
  },

  async created() {
    if (!this.hasAnyReportPermission) {
      this.$router.push("/forbidden");
      return;
    }

    const queryPage = Number(this.$route.query.page);
    const queryType = this.$route.query.type;

    const defaultReportType = this.canViewTaskReports ? "tasks" : "employees";

    this.activeReportType = defaultReportType;

    if (
      (queryType === "tasks" && this.canViewTaskReports) ||
      (queryType === "employees" && this.canViewEmployeeReports)
    ) {
      this.activeReportType = queryType;
    }

    if (Number.isInteger(queryPage) && queryPage > 0) {
      this.paginations.current_page = queryPage;
    }

    await this.setTableRows();
  },
};
</script>

<style scoped>
.report_type_switch {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.report_type_btn {
  border: 1px solid var(--main_theme_clr);
  background: transparent;
  color: var(--main_theme_clr);
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.report_type_btn.active {
  background: var(--main_theme_clr);
  color: #fff;
}
</style>
